import {
  ChangeEvent,
  DragEvent,
  FC,
  useCallback,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { GenericLoading, Sizes } from 'components/ui/Loader';
import { XMarkIcon } from 'components/icons/Icons';
import { CheckCircle2 } from 'lucide-react';
import {
  IdentityVerificationState,
  useIdentityVerification
} from './useIdentityVerification';
import { DropZone } from './components/DropZone';
import { StatusBlock } from './components/StatusBlock';
import { DocumentPreview } from './components/DocumentPreview';
import { ManualForm } from './components/ManualForm';

const ACCEPTED_FORMATS = '.pdf,.jpg,.jpeg,.png';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface IdentityVerificationDialogProps {
  naturalPersonId: string;
  personDisplayName?: string;
  initialStoredFileId?: string;
  onSuccess: (storedFileId: string) => void;
  onClose: () => void;
}

const IdentityVerificationDialog: FC<IdentityVerificationDialogProps> = ({
  naturalPersonId,
  personDisplayName,
  initialStoredFileId,
  onSuccess,
  onClose
}) => {
  const { t } = useTranslation(NsKey.IdentityUpload);
  const { state, uploadFile, submitManual, retryOcr, resetUpload } =
    useIdentityVerification({ naturalPersonId, onSuccess, initialStoredFileId });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleFile = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setLocalError(t('IDENTITY_UPLOAD.ERRORS.FILE_TOO_LARGE', { sizeMb: MAX_FILE_SIZE_MB }));
        return;
      }
      setLocalError(null);
      uploadFile(file);
    },
    [uploadFile, t]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleFile(e.target.files?.[0]);
      e.target.value = '';
    },
    [handleFile]
  );

  const handleBrowse = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFile(e.dataTransfer.files?.[0]);
    },
    [handleFile]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!isDragOver) setIsDragOver(true);
    },
    [isDragOver]
  );

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);

  const handleReset = useCallback(() => {
    setLocalError(null);
    resetUpload();
  }, [resetUpload]);

  const subtitle = personDisplayName
    ? t('IDENTITY_UPLOAD.SUBTITLE_WITH_NAME', { name: personDisplayName })
    : t('IDENTITY_UPLOAD.SUBTITLE_GENERIC');

  return (
    <div className="flex flex-col bg-white rounded-2xl overflow-hidden">
      <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t('IDENTITY_UPLOAD.TITLE')}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg"
          aria-label={t('IDENTITY_UPLOAD.CLOSE')}
        >
          <XMarkIcon className="w-5 h-5 text-gray-600" />
        </button>
      </header>

      <div className="p-6">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={ACCEPTED_FORMATS}
          onChange={handleInputChange}
        />

        <DialogContent
          state={state}
          localError={localError}
          isDragOver={isDragOver}
          maxFileSizeMb={MAX_FILE_SIZE_MB}
          onBrowse={handleBrowse}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onReset={handleReset}
          onRetryOcr={retryOcr}
          onSubmitManual={submitManual}
        />
      </div>
    </div>
  );
};

interface DialogContentProps {
  state: IdentityVerificationState;
  localError: string | null;
  isDragOver: boolean;
  maxFileSizeMb: number;
  onBrowse: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onReset: () => void;
  onRetryOcr: () => void;
  onSubmitManual: (firstName: string, lastName: string) => Promise<void>;
}

const DialogContent: FC<DialogContentProps> = ({
  state,
  localError,
  isDragOver,
  maxFileSizeMb,
  onBrowse,
  onDrop,
  onDragOver,
  onDragLeave,
  onReset,
  onRetryOcr,
  onSubmitManual
}) => {
  const { t } = useTranslation(NsKey.IdentityUpload);

  if (state.status === 'idle' || state.status === 'upload_error') {
    return (
      <DropZone
        isDragOver={isDragOver}
        errorMessage={localError || state.errorMessage}
        maxFileSizeMb={maxFileSizeMb}
        onBrowse={onBrowse}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      />
    );
  }

  if (state.status === 'uploading') {
    return (
      <StatusBlock
        icon={<GenericLoading size={Sizes.lg} loading />}
        title={t('IDENTITY_UPLOAD.DIALOG.UPLOADING_TITLE')}
        description={state.fileName}
      />
    );
  }

  if (state.status === 'ocr_running') {
    return (
      <div className="flex flex-col gap-4">
        <DocumentPreview state={state} compact={false} />
        <StatusBlock
          icon={<GenericLoading size={Sizes.md} loading />}
          title={t('IDENTITY_UPLOAD.DIALOG.OCR_RUNNING_TITLE')}
          description={t('IDENTITY_UPLOAD.DIALOG.OCR_RUNNING_HINT')}
        />
      </div>
    );
  }

  if (state.status === 'success') {
    return (
      <StatusBlock
        icon={<CheckCircle2 className="w-10 h-10 text-emerald-600" />}
        title={t('IDENTITY_UPLOAD.DIALOG.SUCCESS_TITLE')}
        tone="success"
      />
    );
  }

  return (
    <ManualForm
      state={state}
      onReset={onReset}
      onRetryOcr={onRetryOcr}
      onSubmit={onSubmitManual}
    />
  );
};

export default IdentityVerificationDialog;
